import {AxiosRequestConfig} from 'axios';
import {
    Output,
    Mp4OutputFormat,
    MediaStreamVideoTrackSource,
    MediaStreamAudioTrackSource,
    StreamTarget,
	canEncodeVideo,
} from 'mediabunny';
import { Track } from 'livekit-client';
import {
	LocalRecordingDBInstance,
	putChunk,
	upsertRecording,
	markRecordingDone,
	deleteOPFSFile
} from './LocalRecordingStorage';
import {enqueueChunkUpload, startBackgroundUploader, resumePendingOnInit} from './LocalRecordingUploader';
import { requestPersistentStorageOnce } from '../utils/storagePersistence';

const VIDEO_KEYFRAME_INTERVAL_SEC = 2;
const AUDIO_BITRATE_BPS = 192_000;
const MIN_VIDEO_BITRATE_BPS = 2_000_000;
const MAX_VIDEO_BITRATE_BPS = 60_000_000;
let pendingRecoveryStarted = false;

type AuthFetchContext = {
	authAxios: {
		get: (url: string, config?: AxiosRequestConfig) => Promise<{ data: any }>;
		post: (url: string, body?: any, config?: AxiosRequestConfig) => Promise<{ data: any }>;
	}
};

type LocalVideoRecordingOptions = {
	room: any;                        // LiveKit Room
	fetchContext: AuthFetchContext;   // app fetch context (authAxios)
	uploadToken: string;              // Bearer for server calls (long-lived)
	serverTrackId?: string;           // optional server trackId to use immediately (no LiveKit metadata)
	isHDQuality?: boolean;            // bitrate preset: false = standard, true = HD
	fps?: number;                     // default 30
	chunkDurationSec?: number;        // default 30
	width?: number;                   // optional target encode size; if omitted, use video track settings
	height?: number;
	batchSize?: number;               // default 50 (server contract)
	videoSources?: Array<any>;        // default [Track.Source.Camera]
	audioSources?: Array<any>;        // default [Track.Source.Microphone]
	onFatalError?: (error: Error) => void; // called when local recording irrecoverably fails
};

export class LocalVideoRecording {
	private room: any;
	private fetchContext: AuthFetchContext;
	private uploadToken: string;
	private isHD: boolean;
	private fps: number;
	private chunkDurationSec: number;
	private batchSize: number;
	private onFatalError?: (error: Error) => void;
	private didFatalError = false;

	private trackId?: string;
	private serverTrackId?: string;
	private initialServerTrackId?: string;
	private metadataPollInterval?: ReturnType<typeof setInterval>;
	private nextSeq = 0;

	private running = false;
	private stopped = false;
	private segmentLoopPromise?: Promise<void>;
	private segmentAbortController?: AbortController;

	private videoTrack?: MediaStreamTrack;
	private audioTrack?: MediaStreamTrack;

	// Keep local recordings resilient to LiveKit track restarts (device change, unmute reacquire, etc.)
	private localTrackRestartHandlers: Map<any, () => void> = new Map();
	private localTrackToMediaTrack: Map<any, MediaStreamTrack | undefined> = new Map();
	private targetW = 1280;
	private targetH = 720;
	private negotiatedVideoBitrateBps?: number;
	private loggedVideoEncoderConfig = false;
	private loggedAudioEncoderConfig = false;
	private allowedVideoSources: Set<any>;
	private allowedAudioSources: Set<any>;

	constructor(opts: LocalVideoRecordingOptions) {
		this.room = opts.room;
		this.fetchContext = opts.fetchContext;
		this.uploadToken = opts.uploadToken;
		this.isHD = !!opts.isHDQuality;
		this.fps = opts.fps ?? 30;
		this.chunkDurationSec = opts.chunkDurationSec ?? 30;
		this.batchSize = opts.batchSize ?? 50;
		this.onFatalError = opts.onFatalError;
		this.initialServerTrackId = typeof opts.serverTrackId === 'string' ? opts.serverTrackId.trim() : undefined;
		this.allowedVideoSources = new Set((opts.videoSources && opts.videoSources.length > 0) ? opts.videoSources : [Track.Source.Camera]);
		this.allowedAudioSources = new Set((opts.audioSources && opts.audioSources.length > 0) ? opts.audioSources : [Track.Source.Microphone]);
		if (opts.width && opts.height) {
			this.targetW = opts.width;
			this.targetH = opts.height;
		}
	}

	public async start(): Promise<void> {
		if (this.running) throw new Error('LocalVideoRecording already running');
		void requestPersistentStorageOnce('local-recording');
		if (this.initialServerTrackId) {
			this.trackId = this.initialServerTrackId;
			this.serverTrackId = this.initialServerTrackId;
		} else {
			const metaTrackId = this.readMetadataTrackId();
			if (metaTrackId) {
				this.trackId = metaTrackId;
				this.serverTrackId = metaTrackId;
			} else {
				this.trackId = createLocalRecordingId();
				this.serverTrackId = undefined;
			}
		}
		if (!this.serverTrackId) this.startMetadataPoll();

		// Background uploader (idempotent) and resume
			startBackgroundUploader({
				onComplete: async (trackId, totalChunksCount) => {
					const rec = await LocalRecordingDBInstance.recordings.get(trackId);
					const token = rec?.uploadToken ?? this.uploadToken;
					if (!token) return;
					const serverTrackId = resolveServerTrackId(rec);
					if (!serverTrackId) throw new Error('server trackId unavailable for /recording/complete');
					const config: AxiosRequestConfig = { headers: { Authorization: `Bearer ${token}` } };
					await this.fetchContext.authAxios.post('/recording/complete', { trackId: serverTrackId, totalChunksCount }, config);
				}
				});
		// Recovery of pending chunks can be expensive on large local stores.
		// Run it once in the background so current recording start isn't stalled.
		if (!pendingRecoveryStarted) {
			pendingRecoveryStarted = true;
			void resumePendingOnInit().catch((error) => {
				console.warn('[LocalVideoRecording] resumePendingOnInit failed:', error);
				pendingRecoveryStarted = false;
			});
		}

		// If there are existing chunks for this track (resumed), start nextSeq accordingly
		const existing = await LocalRecordingDBInstance.chunks.where('trackId').equals(this.trackId).toArray();
		if (existing.length > 0) {
			this.nextSeq = Math.max(...existing.map(c => c.seq)) + 1;
		} else {
			this.nextSeq = 0;
		}

		// Recording row (store uploadToken for resume)
			await upsertRecording({
				trackId: this.trackId,
				createdAt: Date.now(),
				isRecordingDone: false,
				totalChunksCount: null,
				lastBatchNumberFetched: -1,
				uploadToken: this.uploadToken,
				serverTrackId: this.serverTrackId,
				batchSize: this.batchSize,
			});

		// Attach initial tracks if present
		this.tryAttachInitialVideoTrack();
		this.tryAttachInitialAudioTrack();
		this.hookRoomAudioEvents();
		this.hookRoomVideoEvents();

		this.running = true;
		this.stopped = false;
		// Start segment loop (non-blocking)
		this.segmentLoopPromise = this.segmentLoop().catch((e) => {
			void this.handleFatalError(e);
		});
	}

		public async stop(): Promise<void> {
			this.stopped = true;
			this.running = false;
			this.stopMetadataPoll();
			this.segmentAbortController?.abort();
		// Wait for the segment loop to finish finalizing/persisting any in-flight segment
		try { await this.segmentLoopPromise; } catch {}
		// mark done with total segments
		if (this.trackId != null) {
			const total = this.nextSeq;
			await markRecordingDone(this.trackId, total).catch(() => {});
		}
			this.unhookRoomVideoEvents();
			this.unhookRoomAudioEvents();
			this.unbindAllLocalTrackRestarts();
			this.videoTrack = undefined;
			this.audioTrack = undefined;
		}

	private async handleFatalError(e: any): Promise<void> {
		if (this.didFatalError) return;
		this.didFatalError = true;

		const err: Error = e instanceof Error ? e : new Error(String(e));
		console.error('[LocalVideoRecording] Fatal error; stopping local recording', err);

		try {
			this.onFatalError?.(err);
		} catch {}

		// Best-effort cleanup (don't await segmentLoopPromise here).
		this.stopped = true;
		this.running = false;
		this.stopMetadataPoll();
			try { this.segmentAbortController?.abort(); } catch {}
			this.unhookRoomVideoEvents();
			this.unhookRoomAudioEvents();
			this.unbindAllLocalTrackRestarts();
			this.videoTrack = undefined;
			this.audioTrack = undefined;

		// Finalize the recording row so uploads can complete for already-recorded segments.
		if (this.trackId != null) {
			const total = this.nextSeq;
			await markRecordingDone(this.trackId, total).catch(() => {});
		}
	}

	public onTrackIdReceived(trackId: string): void {
		void this.applyServerTrackId(trackId);
	}

	public getLocalRecordingId(): string | null {
		return this.trackId ?? null;
	}

	private hookRoomVideoEvents() {
		try {
			const RoomEvent = (this.room as any)?.RoomEvent;
			if (typeof this.room?.on === 'function' && RoomEvent?.LocalTrackPublished) {
				this.room.on(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished);
				if (RoomEvent.LocalTrackUnpublished) {
					this.room.on(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished);
				}
			} else if (typeof this.room?.on === 'function') {
				this.room.on('localTrackPublished', this.onLocalTrackPublished);
				this.room.on('localTrackUnpublished', this.onLocalTrackUnpublished);
			}
		} catch {}
	}

	private unhookRoomVideoEvents() {
		try {
			const RoomEvent = (this.room as any)?.RoomEvent;
			if (typeof this.room?.off === 'function' && RoomEvent?.LocalTrackPublished) {
				this.room.off(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished);
				if (RoomEvent.LocalTrackUnpublished) {
					this.room.off(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished);
				}
			} else if (typeof this.room?.off === 'function') {
				this.room.off('localTrackPublished', this.onLocalTrackPublished);
				this.room.off('localTrackUnpublished', this.onLocalTrackUnpublished);
			}
		} catch {}
	}

	private onLocalTrackPublished = (pub: any) => {
		try {
			if (!this.allowedVideoSources.has(pub?.source)) return;
			const lkTrack = pub?.videoTrack;
			const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
			this.bindLocalTrackRestarts(lkTrack, 'video');
			if (track) this.replaceVideoTrack(track);
		} catch {}
	};
	private onLocalTrackUnpublished = (pub: any) => {
		try {
			if (!this.allowedVideoSources.has(pub?.source)) return;
			const lkTrack = pub?.videoTrack;
			this.unbindLocalTrackRestarts(lkTrack);
			const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
			if (track && this.videoTrack === track) {
				this.replaceVideoTrack(undefined);
			}
		} catch {}
	};

	private hookRoomAudioEvents() {
		try {
			const RoomEvent = (this.room as any)?.RoomEvent;
			if (typeof this.room?.on === 'function' && RoomEvent?.LocalTrackPublished) {
				this.room.on(RoomEvent.LocalTrackPublished, this.onLocalAudioTrackPublished);
				if (RoomEvent.LocalTrackUnpublished) {
					this.room.on(RoomEvent.LocalTrackUnpublished, this.onLocalAudioTrackUnpublished);
				}
			} else if (typeof this.room?.on === 'function') {
				this.room.on('localTrackPublished', this.onLocalAudioTrackPublished);
				this.room.on('localTrackUnpublished', this.onLocalAudioTrackUnpublished);
			}
		} catch {}
	}

	private unhookRoomAudioEvents() {
		try {
			const RoomEvent = (this.room as any)?.RoomEvent;
			if (typeof this.room?.off === 'function' && RoomEvent?.LocalTrackPublished) {
				this.room.off(RoomEvent.LocalTrackPublished, this.onLocalAudioTrackPublished);
				if (RoomEvent.LocalTrackUnpublished) {
					this.room.off(RoomEvent.LocalTrackUnpublished, this.onLocalAudioTrackUnpublished);
				}
			} else if (typeof this.room?.off === 'function') {
				this.room.off('localTrackPublished', this.onLocalAudioTrackPublished);
				this.room.off('localTrackUnpublished', this.onLocalAudioTrackUnpublished);
			}
		} catch {}
	}

	private onLocalAudioTrackPublished = (pub: any) => {
		try {
			if (!this.allowedAudioSources.has(pub?.source)) return;
			const lkTrack = pub?.audioTrack;
			const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
			this.bindLocalTrackRestarts(lkTrack, 'audio');
			if (track) this.setAudioTrack(track);
		} catch {}
	};

	private onLocalAudioTrackUnpublished = (pub: any) => {
		try {
			if (!this.allowedAudioSources.has(pub?.source)) return;
			const lkTrack = pub?.audioTrack;
			this.unbindLocalTrackRestarts(lkTrack);
			const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
			if (track && this.audioTrack === track) this.setAudioTrack(undefined);
		} catch {}
	};

	private tryAttachInitialVideoTrack() {
		try {
			const pubs: any = this.room?.localParticipant?.videoTrackPublications;
			if (pubs && typeof pubs.forEach === 'function') {
				let attached = false;
				pubs.forEach((pub: any) => {
					if (!this.allowedVideoSources.has(pub?.source)) return;
					const lkTrack = pub?.videoTrack;
					const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
					this.bindLocalTrackRestarts(lkTrack, 'video');
					if (!attached && track) {
						this.replaceVideoTrack(track);
						attached = true;
					}
				});
			}
		} catch {}
	}

	private tryAttachInitialAudioTrack() {
		try {
			const pubs: any = this.room?.localParticipant?.audioTrackPublications;
			if (pubs && typeof pubs.forEach === 'function') {
				let attached = false;
				pubs.forEach((pub: any) => {
					if (!this.allowedAudioSources.has(pub?.source)) return;
					const lkTrack = pub?.audioTrack;
					const track = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
					this.bindLocalTrackRestarts(lkTrack, 'audio');
					if (!attached && track) {
						this.setAudioTrack(track);
						attached = true;
					}
				});
			}
		} catch {}
	}

	private bindLocalTrackRestarts(lkTrack: any, kind: 'audio' | 'video') {
		if (!lkTrack || typeof lkTrack.on !== 'function') return;
		if (this.localTrackRestartHandlers.has(lkTrack)) return;

		const handler = () => {
			try {
				const next = lkTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
				const prev = this.localTrackToMediaTrack.get(lkTrack);
				if (next === prev) return;
				this.localTrackToMediaTrack.set(lkTrack, next);
				if (kind === 'video') this.replaceVideoTrack(next);
				else this.setAudioTrack(next);
			} catch {}
		};

		this.localTrackToMediaTrack.set(lkTrack, lkTrack.mediaStreamTrack as MediaStreamTrack | undefined);
		try {
			lkTrack.on('restarted', handler);
		} catch {}
		this.localTrackRestartHandlers.set(lkTrack, handler);
	}

	private unbindLocalTrackRestarts(lkTrack: any) {
		if (!lkTrack) return;
		const handler = this.localTrackRestartHandlers.get(lkTrack);
		if (handler) {
			try {
				lkTrack.off?.('restarted', handler);
			} catch {}
			this.localTrackRestartHandlers.delete(lkTrack);
		}
		this.localTrackToMediaTrack.delete(lkTrack);
	}

	private unbindAllLocalTrackRestarts() {
		for (const [lkTrack, handler] of this.localTrackRestartHandlers.entries()) {
			try {
				lkTrack.off?.('restarted', handler);
			} catch {}
		}
		this.localTrackRestartHandlers.clear();
		this.localTrackToMediaTrack.clear();
	}

	private replaceVideoTrack(track?: MediaStreamTrack) {
		const prev = this.videoTrack;
		this.videoTrack = track;
		if (track !== prev) {
			// Track settings (especially frameRate) can change when swapping from placeholders or switching devices.
			// Reset negotiated encoder settings so we don't reuse e.g. 1fps-derived settings from a placeholder track.
			this.negotiatedVideoBitrateBps = undefined;
			this.loggedVideoEncoderConfig = false;
		}
		// If track changes mid-recording, start a fresh segment so the new track is captured
		if (this.running && !this.stopped) {
			this.segmentAbortController?.abort();
		}
	}

	private setAudioTrack(track?: MediaStreamTrack) {
		this.audioTrack = track;
		// If track changes mid-recording, start a fresh segment so the new track is captured
		if (this.running && !this.stopped) {
			this.segmentAbortController?.abort();
		}
	}

    private async segmentLoop(): Promise<void> {
        while (this.running && !this.stopped) {
			const trackId = this.trackId;
			const videoTrack = this.videoTrack;

			// Wait until a video track is available; this can happen shortly after joining/publishing.
			if (!trackId || !videoTrack) {
				await this.sleepMs(100);
				continue;
			}

			const { bitrateBps: videoBitrate } = await this.ensureVideoEncodeSettings(videoTrack);

				// Reserve sequence number up front so we can persist/upload immediately after finalize.
				const seq = this.nextSeq;

			const opfsPath = `/local-recordings/${trackId}/${seq}.mp4`;
			let bytesWritten = 0;

			this.segmentAbortController = new AbortController();
			const segmentAbortController = this.segmentAbortController;
			const segmentSignal = segmentAbortController.signal;
			let stopRequested = false;
			let segmentBaseTimestampSec: number | null = null;
			const maybeRequestStop = (packetTimestampSec: number, packetDurationSec: number) => {
				if (stopRequested) return;
				if (!Number.isFinite(packetTimestampSec) || !Number.isFinite(packetDurationSec)) return;
				if (segmentBaseTimestampSec === null) segmentBaseTimestampSec = packetTimestampSec;
				const endSec = packetTimestampSec + packetDurationSec - segmentBaseTimestampSec;
				if (endSec >= this.chunkDurationSec) {
					stopRequested = true;
					segmentAbortController.abort();
				}
			};

			const writable = await createOpfsWritable(opfsPath);
			const target = new StreamTarget(writable as unknown as WritableStream<any>, { chunked: true });
			target.onwrite = (_start: number, end: number) => {
				bytesWritten = Math.max(bytesWritten, end);
			};

			const output = new Output({ format: new Mp4OutputFormat(), target });

			const videoSource = new MediaStreamVideoTrackSource(videoTrack as any, {
				codec: 'avc',
				bitrate: videoBitrate,
				keyFrameInterval: VIDEO_KEYFRAME_INTERVAL_SEC,
				sizeChangeBehavior: 'contain',
				bitrateMode: 'variable',
				latencyMode: 'realtime',
				onEncodedPacket: (packet: any) => {
					try {
						maybeRequestStop(packet?.timestamp ?? 0, packet?.duration ?? 0);
					} catch {}
				},
				onEncoderConfig: (cfg: VideoEncoderConfig) => {
					if (this.loggedVideoEncoderConfig) return;
					this.loggedVideoEncoderConfig = true;
					console.log('[LocalVideoRecording] VideoEncoderConfig', cfg);
				},
			} as any);
			// Intentionally omit `frameRate` metadata to keep the MP4 timebase stable across segments
			// (e.g. when switching from low-fps placeholder video to real camera video).
			output.addVideoTrack(videoSource);

			const audioTrack = this.audioTrack;
			let audioSource: MediaStreamAudioTrackSource | null = null;
			if (audioTrack) {
				audioSource = new MediaStreamAudioTrackSource(audioTrack as any, {
					codec: 'aac',
					bitrate: AUDIO_BITRATE_BPS,
					bitrateMode: 'variable',
					onEncoderConfig: (cfg: AudioEncoderConfig) => {
						if (this.loggedAudioEncoderConfig) return;
						this.loggedAudioEncoderConfig = true;
						console.log('[LocalVideoRecording] AudioEncoderConfig', cfg);
					},
				} as any);
				output.addAudioTrack(audioSource);
			}

			const endPromise = Promise.race([
				this.sleepMs(this.chunkDurationSec * 1000, segmentSignal),
				videoSource.errorPromise,
				audioSource ? audioSource.errorPromise : new Promise<void>(() => {}),
			]);

			try {
				await output.start();
				await endPromise;
			} catch (e) {
				console.error('[LocalVideoRecording] Segment encoder error', e);
			} finally {
				try {
					await output.finalize();
				} catch (e) {
					console.warn('[LocalVideoRecording] Segment finalize error', e);
				}
			}

			// If we stopped immediately (or got no data), don't emit an empty file.
			if (bytesWritten <= 0) {
				await deleteOPFSFile(opfsPath).catch(() => {});
				if (this.stopped) break;
				continue;
			}

			const id = `${trackId}_${seq}`;
				await putChunk({
					id,
					trackId,
					seq,
					createdAt: Date.now(),
					opfsPath,
					signedUrl: '',
					status: 'pending',
					attemptCount: 0,
				});
			enqueueChunkUpload(id, trackId);
			this.nextSeq = seq + 1;
        }
    }

	private async ensureVideoEncodeSettings(videoTrack: MediaStreamTrack): Promise<{
		bitrateBps: number;
	}> {
		if (this.negotiatedVideoBitrateBps) {
			return { bitrateBps: this.negotiatedVideoBitrateBps };
		}

		const settings = safeGetTrackSettings(videoTrack);
		const width = toEvenInt(settings.width ?? this.targetW);
		const height = toEvenInt(settings.height ?? this.targetH);
		const frameRate = Math.max(1, Math.round(settings.frameRate ?? this.fps));
		const initialBitrate = clampInt(
			suggestVideoBitrateBps({ width, height, frameRate, isHD: this.isHD }),
			MIN_VIDEO_BITRATE_BPS,
			MAX_VIDEO_BITRATE_BPS,
		);

		const bitrateBps = await pickSupportedAvcBitrate({
			width,
			height,
			frameRate,
			targetBitrateBps: initialBitrate,
		});

		this.negotiatedVideoBitrateBps = bitrateBps;
		console.log('[LocalVideoRecording] Using H.264 settings', {
			width,
			height,
			frameRate,
			bitrateBps,
			keyFrameIntervalSec: VIDEO_KEYFRAME_INTERVAL_SEC,
		});

		return { bitrateBps };
	}

	private async sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
		if (ms <= 0) return;
		if (signal?.aborted) return;

		await new Promise<void>((resolve) => {
			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				resolve();
			};

			let onAbort: (() => void) | null = null;

			const timer = setTimeout(() => {
				if (signal && onAbort) signal.removeEventListener('abort', onAbort);
				finish();
			}, ms);
			if (!signal) return;

			onAbort = () => {
				clearTimeout(timer);
				if (onAbort) signal.removeEventListener('abort', onAbort);
				finish();
			};
			signal.addEventListener('abort', onAbort);
		});
	}

		private readMetadataTrackId(): string | undefined {
			try {
				const meta = this.room?.localParticipant?.metadata;
				if (meta && typeof meta === 'string' && meta.trim() !== '') return meta.trim();
			} catch {}
			return undefined;
		}

		private startMetadataPoll() {
			if (this.metadataPollInterval) return;
			this.metadataPollInterval = setInterval(() => {
				if (this.serverTrackId) return;
				const meta = this.readMetadataTrackId();
				if (meta) this.onTrackIdReceived(meta);
			}, 500);
		}

		private stopMetadataPoll() {
			if (!this.metadataPollInterval) return;
			clearInterval(this.metadataPollInterval);
			this.metadataPollInterval = undefined;
		}

		private async applyServerTrackId(trackId: string): Promise<void> {
			const clean = typeof trackId === 'string' ? trackId.trim() : '';
			if (!clean) return;
			if (this.serverTrackId === clean) return;
			this.serverTrackId = clean;
			this.stopMetadataPoll();

			if (!this.trackId) return;
			await LocalRecordingDBInstance.recordings.update(this.trackId, { serverTrackId: clean }).catch(() => {});

			// Kick the uploader for any already-persisted segments waiting on serverTrackId.
			try {
				const chunks = await LocalRecordingDBInstance.chunks.where('trackId').equals(this.trackId).toArray();
				for (const c of chunks) {
					enqueueChunkUpload(c.id, this.trackId);
				}
			} catch {}
	}
}

function createLocalRecordingId(): string {
	try {
		if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
			return `local-${(crypto as any).randomUUID()}`;
		}
	} catch {}
	return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function resolveServerTrackId(rec: any): string | undefined {
	if (!rec) return undefined;
	const candidate = rec.serverTrackId;
	if (candidate && typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
	if (rec.trackId && typeof rec.trackId === 'string' && !rec.trackId.startsWith('local-')) return rec.trackId;
	return undefined;
}

function safeGetTrackSettings(track: MediaStreamTrack): MediaTrackSettings {
	try {
		if (typeof track?.getSettings === 'function') {
			return track.getSettings();
		}
	} catch {}
	return {};
}

function toEvenInt(value: number): number {
	const n = Math.max(2, Math.floor(value));
	return n % 2 === 0 ? n : n - 1;
}

function clampInt(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, Math.round(value)));
}

function suggestVideoBitrateBps(opts: { width: number; height: number; frameRate: number; isHD: boolean }): number {
	const pixels = opts.width * opts.height;
	let baseBps: number;

	// Conservative presets to keep encoding + upload realtime-friendly.
	if (pixels >= 3840 * 2160) {
		baseBps = opts.isHD ? 25_000_000 : 20_000_000;
	} else if (pixels >= 2560 * 1440) {
		baseBps = opts.isHD ? 16_000_000 : 12_000_000;
	} else if (pixels >= 1920 * 1080) {
		baseBps = opts.isHD ? 12_000_000 : 8_000_000;
	} else if (pixels >= 1280 * 720) {
		baseBps = opts.isHD ? 6_000_000 : 5_000_000;
	} else {
		baseBps = opts.isHD ? 4_000_000 : 3_000_000;
	}

	const fpsScale = opts.frameRate / 30;
	const scaled = Math.round(baseBps * fpsScale);
	// Round to 100kbps for stable codec-string selection + nicer logs.
	return Math.round(scaled / 100_000) * 100_000;
}

async function pickSupportedAvcBitrate(opts: {
	width: number;
	height: number;
	frameRate: number;
	targetBitrateBps: number;
}): Promise<number> {
	const candidates: number[] = [];
	const target = opts.targetBitrateBps;

	// Try target first, then step down. If the browser rejects our encoder config, this usually helps.
	for (const factor of [1, 0.85, 0.7, 0.55, 0.4]) {
		candidates.push(clampInt(target * factor, MIN_VIDEO_BITRATE_BPS, MAX_VIDEO_BITRATE_BPS));
	}

	// De-dup while preserving order.
	const unique: number[] = [];
	for (const c of candidates) {
		if (!unique.includes(c)) unique.push(c);
	}

	for (const bitrate of unique) {
		try {
			const ok = await canEncodeVideo('avc', {
				width: opts.width,
				height: opts.height,
				bitrate,
				bitrateMode: 'variable',
				latencyMode: 'realtime',
			});
			if (ok) return bitrate;
		} catch {}
	}

	// Fall back to whatever we computed; the encoder may still succeed even if preflight was pessimistic.
	return target;
}

async function createOpfsWritable(path: string): Promise<FileSystemWritableFileStream> {
	// @ts-ignore - not yet in TS lib.dom.d.ts in all TS versions
	let dir: FileSystemDirectoryHandle = await navigator.storage.getDirectory();
	const parts = path.split('/').filter(Boolean);
	const fileName = parts.pop();
	if (!fileName) throw new Error('Invalid OPFS path');
	for (const seg of parts) {
		dir = await dir.getDirectoryHandle(seg, { create: true });
	}
	const fh = await dir.getFileHandle(fileName, { create: true });
	return await fh.createWritable();
}
